import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import i18n from '../i18n.js'
import { api } from '../api.js'

// AuthContext: source of truth for "who is logged in".
//
// Auth is passwordless email OTP — the Login page calls api.auth.requestCode
// and api.auth.verifyCode directly; this context just tracks the resulting
// session via /api/me and offers a `setUser` so the Login page can flip the
// context in-memory after a successful verify (no extra round-trip).
//
// On mount we call /api/me. If logged in, the response includes the user's
// preferred `language` and we hand it to i18next so UI + emails stay aligned.

const AuthContext = createContext({
  user: null,
  loading: true,
  setUser: () => {},
  logout: async () => {},
})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    api.me()
      .then((data) => {
        if (!alive) return
        setUser(data.user)
        if (data.user?.language && data.user.language !== i18n.language) {
          i18n.changeLanguage(data.user.language)
        }
      })
      .catch(() => { /* not authenticated; user stays null */ })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const adoptUser = useCallback((u) => {
    setUser(u)
    if (u?.language) i18n.changeLanguage(u.language)
  }, [])

  const logout = useCallback(async () => {
    try { await api.logout() } catch { /* best effort */ }
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, setUser: adoptUser, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
